import React from 'react'

// Icons
import calendar from 'icons/calendar.png'
import calendar2x from 'icons/calendar@2x.png'
import documentation from 'icons/documentation.png'
import documentation2x from 'icons/documentation@2x.png'
import documents from 'icons/documents.png'
import documents2x from 'icons/documents@2x.png'
import subscription from 'icons/subscription.png'
import subscription2x from 'icons/subscription@2x.png'
import persons from 'icons/persons.png'
import persons2x from 'icons/persons@2x.png'
import email from 'icons/email.png'
import email2x from 'icons/email@2x.png'

// Components
import Button, { LinkButton } from 'components/Button'

// Utils
import { setSketchUrlScheme } from 'utils'

// Styles
import { Icon } from './BannerCardList.styles'
import { routes } from 'constants/routes'
import { useToast } from 'containers/ToastManager'

// Types
import { useResendVerificationEmailMutation } from 'gqlTypes'
import { BannerCardProps } from 'components/BannerCard/BannerCard'

export type BannerType = BannerCardProps

const ResendEmailButton = () => {
  const { showToast } = useToast()

  const [resendVerificationEmail] = useResendVerificationEmailMutation({
    redirectErrors: true,
    onCompleted: () => showToast(`An email has been sent`),
    onError: 'show-toast',
  })

  return (
    <Button
      variant="secondary-untinted"
      small
      onClick={() => resendVerificationEmail()}>
      Resend Email
    </Button>
  )
}

export const VERIFY_EMAIL_BANNER = {
  id: 'verifyEmailBanner',
  isDismissable: false,
  icon: (
    <Icon
      src={email2x}
      srcSet={`${email2x} 2x, ${email} 1x`}
      alt="Verify Your Email"
    />
  ),
  title: 'Verify Your Email',
  description: `Check your inbox and confirm your email`,
  action: <ResendEmailButton />,
}

export const DOCUMENTATION_BANNER = {
  id: 'documentationBanner',
  isDismissable: false,
  icon: (
    <Icon
      src={documentation2x}
      srcSet={`${documentation2x} 2x, ${documentation} 1x`}
      alt="Get Better at Sketch"
    />
  ),
  title: 'Get Better at Sketch',
  description:
    'Beginner or expert, here’s everything you need to make the most of Sketch',
  action: (
    <LinkButton
      variant="secondary-untinted"
      small
      to={{ pathname: 'https://www.sketch.com/docs/' }}
      target="_blank">
      View Documentation
    </LinkButton>
  ),
}

export const UPLOAD_DOCUMENTS_BANNER = {
  id: 'uploadDocumentsBanner',
  isDismissable: false,
  icon: (
    <Icon
      src={documents2x}
      srcSet={`${documents2x} 2x, ${documents} 1x`}
      alt="Save a Document"
    />
  ),
  title: 'Save a Document to the Workspace',
  description:
    'Create and save a document in the Mac App to see it in your Workspace',
  action: (
    <LinkButton
      variant="secondary-untinted"
      small
      to={{ pathname: setSketchUrlScheme(`sketch.cloud/`) }}
      target="_blank">
      Open the Mac App
    </LinkButton>
  ),
}

export const DOWNLOAD_LATEST_APP_BANNER = {
  id: 'downloadLatestBanner',
  isDismissable: false,
  icon: (
    <Icon
      src={calendar2x}
      srcSet={`${calendar2x} 2x, ${calendar} 1x`}
      alt="Download the Mac App"
    />
  ),
  title: 'Download the Mac App',
  description: 'Download the latest version of the Mac app',
  action: (
    <LinkButton
      variant="secondary-untinted"
      small
      to={{
        pathname: 'https://sketch.com/downloads/mac',
      }}
      target="_blank">
      Download
    </LinkButton>
  ),
}

export const COLLABORATION_BANNER = {
  id: 'collaborationBanner',
  isDismissable: false,
  icon: (
    <Icon
      src={persons2x}
      srcSet={`${persons2x} 2x, ${persons} 1x`}
      alt="Invite People"
    />
  ),
  title: 'Start Collaborating',
  description: 'Invite people as Editors or Viewers and start collaborating',
  action: (
    <LinkButton
      variant="secondary-untinted"
      small
      to={routes.WORKSPACE_AGNOSTIC_SETTINGS_GENERAL.create({})}>
      Invite People
    </LinkButton>
  ),
}

export const SUBSCRIBE_BANNER = {
  id: 'subscribeBanner',
  isDismissable: false,
  icon: (
    <Icon
      src={subscription2x}
      srcSet={`${subscription2x} 2x, ${subscription} 1x`}
      alt="Subscribe to Sketch"
    />
  ),
  title: 'Subscribe to Sketch',
  description: 'Subscribe to keep using Sketch after the trial ends',
  action: (
    <LinkButton
      variant="secondary-untinted"
      small
      to={routes.WORKSPACE_AGNOSTIC_SETTINGS_BILLING.create({})}>
      Set Up Subscription
    </LinkButton>
  ),
}

export const UPGRADE_PERSONAL_WORKSPACE_BANNER = {
  id: 'upgradePersonalWorkspaceBanner',
  isDismissable: true,
  icon: (
    <Icon
      src={subscription2x}
      srcSet={`${subscription2x} 2x, ${subscription} 1x`}
      alt="Upgrade Your Legacy Workspace"
    />
  ),
  title: 'Upgrade Your Legacy Workspace',
  description: 'Switch to a subscription, starting with a free trial',
  action: (
    <LinkButton
      variant="secondary-untinted"
      small
      to={routes.WORKSPACE_AGNOSTIC_SETTINGS_GENERAL.create({})}>
      Start Upgrade
    </LinkButton>
  ),
}
